import React, { createContext, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import useInterval from 'react-useinterval';
import { CustomError, isNetworkError, TechnicalError } from '../common/errors';
import Result from '../common/result';
import WindowProvider from '../common/window';
import { ValidationStep } from '../services/validation';
import authenticationService, { ExchangeTokenStatus } from '../services/authentication';
import { ValidationContext, getPollingDelayMilliseconds } from './ValidationContext';
import { FlowConfigContext } from './FlowConfigContext';
import documentRouting from './validation/validationRouting';
import { isValidationFailedError } from './validation/validationError';
import { MemoryRouterContext } from './MemoryRouterContext';
import { useCredentialStore } from './CredentialStoreContext';
import { useScopeRequest } from './ScopeRequestContext';
import { Action } from '../config';

/** Exchange token fetch statuses */
export enum ExchangeTokenFetchStatus {
  /** The token is being fetched */
  fetching = 'fetching',
  /** The token is in a failed status and needs to be fetched again */
  failedRetry = 'failedRetry',
}

export interface LinkDevicesProps {
  /** Generate a link to continue session in a different browser */
  generateLink(): Promise<Result<CustomError, string>>;
  /** Clear current link and stop all polling */
  clearCurrentLink(): void;
  /** The status of the exchange token to link devices */
  exchangeTokenStatus: ExchangeTokenStatus | ExchangeTokenFetchStatus;
  /** A flag indicating if the pending continuation steps are completed */
  pendingStepsCompleted: boolean;
  /** A flag indicating if the process went into an error state when polling for changes */
  pendingStepsFailed: boolean;
}

export const LinkDevicesContext = createContext<LinkDevicesProps>({} as LinkDevicesProps);

type LinkDevicesProviderProps = {
  children: JSX.Element;
};

/**
 *
 * @param props
 * @constructor
 */
export function LinkDevicesProvider(props: LinkDevicesProviderProps): JSX.Element {
  const { children } = props;
  const { queryParams } = useContext(MemoryRouterContext);
  const { networkAddress, action, credentialExpired } = useContext(FlowConfigContext);
  const { validationId, getPendingContinueOnMobileRoutes, updateValidationPendingSteps } =
    useContext(ValidationContext);
  const { chosenCredentialIds } = useCredentialStore();

  const [pendingSteps, setPendingSteps] = useState<ValidationStep[]>([]);
  const [pendingStepsCompleted, setPendingStepsCompleted] = useState<boolean>(false);
  const [pendingStepsFailed, setPendingStepsFailed] = useState<boolean>(false);
  const [exchangeToken, setExchangeToken] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<ExchangeTokenStatus | ExchangeTokenFetchStatus>(
    ExchangeTokenFetchStatus.fetching,
  );
  const [pollingDelay, setPollingDelay] = useState<number | null>(null);
  const { scopeRequest } = useScopeRequest();

  // Set pending steps completed when the token is activated and the pending steps array is empty
  useEffect(() => {
    if (R.equals(tokenStatus, ExchangeTokenStatus.activated)) {
      setPendingStepsCompleted(R.isEmpty(pendingSteps));
      return;
    }

    setPendingStepsCompleted(false);
  }, [tokenStatus, pendingSteps]);

  // Start polling if token is pending or pending steps are not completed
  useEffect(() => {
    const tokenIsPending = tokenStatus === ExchangeTokenStatus.pending;
    const validationHasPendingSteps = tokenStatus === ExchangeTokenStatus.activated && !R.isEmpty(pendingSteps);
    if (tokenIsPending || validationHasPendingSteps) {
      setPollingDelay(getPollingDelayMilliseconds());
    }
  }, [tokenStatus, pendingSteps]);

  /** Poll for the latest exchange token status and return a flag indicating if polling should continue */
  const pollForTokenStatus = async (): Promise<boolean> => {
    if (!exchangeToken) {
      // stop polling if there is no exchange token
      return false;
    }

    const getExchangeTokenResult = await authenticationService.getExchangeToken({ exchangeToken });
    if (getExchangeTokenResult.isFailed()) {
      getExchangeTokenResult.logError('LinkDevicesProvider: Error polling for exchange token result');
      // If fetching the token returns a 404 error, then the token has expired and a new token needs to be requested
      const isTokenMissingError = getExchangeTokenResult.failedWith(isNetworkError(404));
      if (isTokenMissingError) {
        // Set the status to failed retry to allow the user to fetch a new token
        setTokenStatus(ExchangeTokenFetchStatus.failedRetry);
      }

      // retry polling if this is not a token missing error
      return !isTokenMissingError;
    }

    const latestExchangeToken = getExchangeTokenResult.success();
    // keep polling if the token status is unchanged
    const keepPolling = latestExchangeToken.status === tokenStatus;
    setTokenStatus(latestExchangeToken.status);
    return keepPolling;
  };

  /** Poll for the latest pending steps and return a flag indicating if polling should continue */
  const pollForPendingStepsStatus = async (): Promise<boolean> => {
    const updatedStepsResult = await updateValidationPendingSteps();
    if (updatedStepsResult.isFailed()) {
      const validationIsFailed = updatedStepsResult.failedWith(isValidationFailedError);
      setPendingStepsFailed(validationIsFailed);
      // Only keep polling if the validation is not failed
      return !validationIsFailed;
    }

    const updatedSteps = updatedStepsResult.success();
    const awaitingSteps = R.intersection(pendingSteps, updatedSteps);
    // keep polling if the pending steps are unchanged
    const keepPolling = R.equals(awaitingSteps, pendingSteps);
    setPendingSteps(updatedSteps);
    return keepPolling;
  };

  useInterval(async () => {
    // clear polling delay while we are fetching the latest status
    setPollingDelay(null);

    let keepPolling = false;
    if (!R.equals(ExchangeTokenStatus.activated, tokenStatus)) {
      keepPolling = await pollForTokenStatus();
    } else if (!R.isEmpty(pendingSteps)) {
      keepPolling = await pollForPendingStepsStatus();
    }

    if (keepPolling) {
      setPollingDelay(getPollingDelayMilliseconds());
    }
  }, pollingDelay);

  async function generateLink(): Promise<Result<CustomError, string>> {
    if (!validationId) {
      return Result.failed(new TechnicalError('Unexpected null validation Id on generateLink'));
    }

    setTokenStatus(ExchangeTokenFetchStatus.fetching);
    // Get all steps for pending continue on mobile routes. Return the
    // pending continue on mobile routes, and then get all corresponding steps
    // for those routes. This ensures that the continue on mobile session
    // has all the relevant steps included
    const pendingStepsForLink = R.pipe(
      R.map(documentRouting.getAllStepsForRoute),
      R.flatten,
    )(getPendingContinueOnMobileRoutes());

    // When we're refreshing an expired credential it's an issuance flow from the user's perspective, so set this in
    // the handover request, otherwise default to the original action, which can be Action.ISSUANCE or Action.REFRESH.
    const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;

    const exchangeTokenResult = await authenticationService.requestExchangeToken({
      networkAddress,
      validationId,
      steps: pendingStepsForLink,
      sourceAction: flowAction,
      reuseCredentials: chosenCredentialIds.length > 0,
      scopeRequestId: scopeRequest?.uuid,
    });

    if (exchangeTokenResult.isFailed()) {
      exchangeTokenResult.logError('LinkDevicesProvider: Error requesting exchange token');
      setTokenStatus(ExchangeTokenFetchStatus.failedRetry);
      return Result.failed(exchangeTokenResult.failure());
    }

    const token = exchangeTokenResult.success();
    setExchangeToken(token.exchangeToken);
    setTokenStatus(token.status);
    setPendingSteps(pendingStepsForLink);

    const searchParams = new URLSearchParams({
      ...R.pick(
        ['flowId', 'flowType', 'did', 'domain', 'wallet', 'chain', 'chainNetwork', 'networkAddress'],
        queryParams,
      ),
      action: 'link',
      token: token.exchangeToken,
    });
    return Result.successful(`${WindowProvider.href()}?${searchParams.toString()}`);
  }

  function clearCurrentLink(): void {
    setExchangeToken(null);
    setTokenStatus(ExchangeTokenFetchStatus.fetching);
    setPendingSteps([]);
  }

  return (
    <LinkDevicesContext.Provider
      value={{
        generateLink,
        clearCurrentLink,
        exchangeTokenStatus: tokenStatus,
        pendingStepsCompleted,
        pendingStepsFailed,
      }}
    >
      {children}
    </LinkDevicesContext.Provider>
  );
}
